import React, { useState } from "react"
import { toast } from "react-toastify"
import { useParams, useNavigate, useSearchParams } from "react-router-dom"
import { FormattedMessage, useIntl } from "react-intl"
import classNames from "classnames"
import { useDispatch } from "react-redux"

import { deleteJobs } from "../../../services/jobs"

// Redux
import { removeJob } from "../../../redux/slices/jobSlice"

// Components
import { CustomLink, Spinner } from "../../common"
import { Candidate } from "./components"

// Constants
import { GA_EVENTS } from "../../../constants/analytics"
import { JOB_STATUS, JOB_MATCHING_STATUS } from "../../../constants"
import { useData } from "../../../hooks/useData"

const MatchesPage = () => {
  const { jobId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { formatMessage } = useIntl()

  const [isDeletingJob, setIsDeletingJob] = useState(false);

  const { isFetching, jobs, jobMatchingStatus, candidates } = useData({});

  const {
    jobStatus,
    noRequirementsSet,
  } = jobs

  const selectedCandidate = searchParams.get("candidate")
  const tab = searchParams.get("tab") || "job-match"

  if (isFetching) {
    return (
      <div className={"matches-page"}>
        <Spinner />
      </div>
    )
  }

  if (!jobId && !jobs?.length) {
    return (
      <div className={"matches-page"}>
        <p><FormattedMessage id="common.noMatchingResultYet" /></p>
        <p><FormattedMessage id="common.pleaseGoBackCVs" /></p>
      </div>
    )
  }

  if (!jobId) {
    return (
      <div className={"matches-page"}>
        <p><FormattedMessage id="common.pleaseSelectAJob" /></p>
      </div>
    )
  }

  const candidateMatchInfo = candidates?.find(
    (candidate) => candidate.id === selectedCandidate,
  )

  const switchTab = (tab) => {
    setSearchParams({
      tab: tab,
      candidate: selectedCandidate,
    })
  }

  const handleDeleteJob = async () => {
    setIsDeletingJob(true);
    try {
      await deleteJobs([jobId]);
      dispatch(removeJob(jobId));
      navigate('/job-match/matches'); // Redirect to next available match
      toast.success(formatMessage({ id: "toast.jobsDeletedSuccessfully" }));
    } catch (error) {
      console.error('Failed to delete job:', error);
      toast.error(formatMessage({ id: "toast.failedToDeleteJobs" }));
      // Optionally show an error message to the user
    } finally {
      setIsDeletingJob(false);
    }
  };

  if (jobMatchingStatus === JOB_MATCHING_STATUS.NO_MATCHING_RESULT || noRequirementsSet) {
    return (
      <div className="matches-page">
        <div className="matches-page__card">
          <p className="h4">
            <FormattedMessage id="common.stillWorkingOnJobMatch" />
          </p>
          <p className="matches-page__card-text p">
            <FormattedMessage id="message.finishCheckingRequirements" />
          </p>

          <div className="matches-page__card-actions">
            <CustomLink
              className={classNames("btn primary btn--small", {
                disabled: isDeletingJob,
              })}
              to={`/job-match/${jobId}/requirements`}
              trackingEvent={GA_EVENTS.JOB_MATCHES.BACK_AND_CONTINUE}
              disabled={isDeletingJob}
            >
              <span>
                <FormattedMessage id="message.goBack" />{" "}
                <span>
                  {" "}
                  <FormattedMessage id="message.andContinue" />
                </span>
              </span>
            </CustomLink>

            <button
              className={classNames("btn warning btn--small", {
                disabled: isDeletingJob,
              })}
              onClick={handleDeleteJob}
              disabled={isDeletingJob}
            >
              <FormattedMessage id="common.delete" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={classNames("matches-page", {
        "matches-page__loading":
          !candidates ||
          jobMatchingStatus === JOB_MATCHING_STATUS.NO_MATCHING_RESULT,
      })}
    >
      {(jobMatchingStatus === JOB_MATCHING_STATUS.MATCHED && candidates.length === 0) && (
        <div className="matches-page__info-container">
          <h2>
            <FormattedMessage id="common.noMatchFound" />
          </h2>
          <p>
            <FormattedMessage id="common.tryChangingRequirementsJobDescription" />
          </p>
        </div>
      )}
      {jobMatchingStatus === JOB_MATCHING_STATUS.NOT_PERFORM_MATCHING_YET && (
        <div className="matches-page__info-container">
          <h2>
            <FormattedMessage id="common.somethingWrongWhileMatching" />
          </h2>
        </div>
      )}
      {jobMatchingStatus === JOB_MATCHING_STATUS.MATCHING_IN_PROGRESS && (
        <div className="matches-page__info-container">
          <h2>
            <FormattedMessage id="common.matchingInProgress" />
          </h2>
        </div>
      )}
      {jobStatus === JOB_STATUS.IN_PROGRESS && (
        <div className="matches-page__info-container">
          <h2>
            <FormattedMessage id="common.matchingCandidatesToRequirements" />
          </h2>
          <Spinner variant="secondary" />
        </div>
      )}
      {!candidates && (
        <div>
          <Spinner />
        </div>
      )}

      {candidateMatchInfo && (
        <Candidate
          key={selectedCandidate}
          tab={tab}
          switchTab={switchTab}
          matchInfo={candidateMatchInfo}
        />
      )}
    </div>
  )
}

export default MatchesPage
